import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import RightArrow from '../../assets/images/left-arrow-symbol.svg';
import SwitchBar from '../../components/switch-bar';
import { Paginator } from '../../components/common/paginator';
import AlertBox from '../../components/route-leaving-prompt/alert-box';
import Modal from '../../components/common/modal';
import { useForm } from 'react-hook-form';
import { Tag, TagRequestParams, useCreateTag, useDeleteTag, useTagsV3, useUpdateTag } from '../../hooks/useTags';
import { CustomInput, UseFormDropDown, UseFormToggle } from '../../components/common';
import { onBlur } from '../../components/common/useForm';
import Searchbar from '../../components/common/searchbar';
import { TagTypes } from './tag-groups';

export const Headeroptions = [
  { heading: 'Tag Groups', path: '/tags/tag-groups' },
  { heading: 'Tags', path: '/tags' },
];

const Tags: React.FunctionComponent = (): JSX.Element => {
  const [filters, setFilters] = useState<TagRequestParams>({
    include_hidden: true,
    limit: 25,
    page: 1,
    tag_type: undefined,
    search: '',
  });
  const { data: tagsList, isLoading } = useTagsV3(filters);
  const { mutate: deleteTag } = useDeleteTag();
  const [selectedTag, setSelectedTag] = useState<Tag | undefined>(tagsList?.data?.[0] || undefined);
  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState<boolean>(false);

  const handleDelete = () => {
    setShowAlertModal(false);
    deleteTag(selectedTag?.id!);
  };

  const handleSelectedTag = (tag?: Tag) => {
    if (tag) {
      setSelectedTag(tag);
    } else {
      setSelectedTag(tagsList?.data?.[0]);
    }
  };

  useEffect(() => {
    handleSelectedTag();
  }, [tagsList]);

  return (
    <div>
      <Header heading={'Tags'} />
      <SwitchBar heading={Headeroptions} position={1} />
      <Filters
        count={tagsList?.count || undefined}
        filters={filters}
        setFilters={setFilters}
        setShowCreateTagModal={setShowCreateTagModal}
      />
      {isLoading && <Spinner />}
      {tagsList && selectedTag && (
        <div className="flex w-full">
          <TagsList selectedTagId={selectedTag.id} setSelectedTag={setSelectedTag} tags={tagsList.data || []} />
          <TagDetails tag={selectedTag} setShowAlertModal={setShowAlertModal} />
        </div>
      )}
      {showCreateTagModal && <CreateTagModal setShowCreateTagModal={setShowCreateTagModal} />}
      <AlertBox
        visible={showAlertModal}
        handlePrompt={handleDelete}
        closeModal={() => setShowAlertModal(false)}
        titleText={'Alert'}
        contentText={`Are you sure you want to remove the selected Tag Group?`}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Remove'}
      />
    </div>
  );
};

interface TagsListProps {
  selectedTagId: number;
  setSelectedTag: (tag: Tag) => void;
  tags: Tag[];
}

const TagsList: React.FC<TagsListProps> = ({ selectedTagId, setSelectedTag, tags }) => {
  return (
    <div className="w-1/2 px-7">
      <div className="">
        <div className="text-gray-dark sticky mb-2 grid h-12 w-full grid-cols-12 items-center border-b-2 border-black text-left font-bold">
          <div className="col-span-5 font-bold">
            <p className="pl-4">Tag Name</p>
          </div>
          <div className="col-span-3">Type </div>
          <div className="col-span-4">Status </div>
        </div>
      </div>
      <div className="pb-10">
        {tags?.slice().map((tag: Tag) => (
          <div
            className={`w-full ${selectedTagId === tag.id ? 'bg-blue-primary text-white' : 'text-gray-dark bg-gray-200'} my-2 grid h-20 cursor-pointer grid-cols-12 items-center rounded-xl border-y text-left`}
            onClick={() => {
              setSelectedTag(tag);
            }}
          >
            <div className="col-span-5 font-bold">
              <p className="pl-4 pr-2">{tag.name}</p>
            </div>
            <div className="col-span-3 capitalize"> {tag.tag_type} </div>
            <div className="col-span-4 mr-2 flex items-center justify-between">
              <div> {`${tag.is_visible ? 'Visible' : 'Hidden'}`}</div>
              <button className="blue-primary h-8 w-8 rotate-180 transform rounded-full">
                <img className="h-8 w-8" src={RightArrow} alt="left Arrow"></img>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface TagDetailsProps {
  tag: Tag;
  setShowAlertModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const TagDetails: React.FC<TagDetailsProps> = ({ tag, setShowAlertModal }) => {
  const { mutate: updateTag } = useUpdateTag();

  const defaultTag = {
    ...tag,
    is_background: tag.is_background || false,
    is_default: tag.is_default || false,
    is_required: tag.is_required || false,
    is_visible: tag.is_visible || false,
    name: tag.name || '',
    tag_type: tag.tag_type || 'topic',
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      if (tag) {
        return defaultTag;
      }
    }, [tag]),
  });

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      id: tag.id,
      is_visible: data.is_visible === 'true' ? true : false,
    };
    updateTag(payload);
  };

  useEffect(() => {
    if (tag) {
      reset(tag);
    }
  }, [tag, reset]);

  return (
    <div className="h-162.5 mx-7 my-12 w-1/2 rounded-3xl border-2 border-gray-200">
      <div className="flex h-10 items-center justify-between px-6 pt-2">
        <button
          className="bg-red h-6 w-20 rounded-full px-2 text-center text-sm text-white"
          onClick={() => {
            setShowAlertModal(true);
          }}
        >
          REMOVE
        </button>
        <div className={` ${!isDirty && 'cursor-not-allowed'}`}>
          <button
            className={`ml-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white ${
              isDirty ? 'bg-bright-blue' : 'bg-gray-background-dark pointer-events-none'
            }`}
            type="submit"
            form="update-tag-group"
          >
            SAVE
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} id="update-tag-group">
        <div className="w-full px-3">
          <div className="mt-5 grid w-full grid-cols-1 gap-x-6 gap-y-6 px-7">
            <div className="flex flex-col space-y-4">
              <CustomInput
                inputType="text"
                Label="Tag Name"
                register={{
                  ...register('name', {
                    required: 'Please provide Tag name',
                    onBlur: (e: any) => onBlur(e, 'name', setValue),
                  }),
                }}
                Error={errors?.name?.message?.length ? true : false}
                ErrorMessage={errors.name?.message}
                dataTestId="tag-name"
              />
              <div className="flex space-x-2">
                <p>Tag Type:</p>
                <p className="font-bold capitalize">{tag.tag_type}</p>
              </div>
              <UseFormDropDown label={'Status'}>
                <select
                  className="select-box w-full focus:ring-transparent"
                  defaultValue="unpublished"
                  {...register('is_visible')}
                  data-testid="tag-details-status"
                >
                  <option value={'true'}>Visible</option>
                  <option value={'false'}>Hidden</option>
                </select>
              </UseFormDropDown>
            </div>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-blue-dark mb-2 mr-2 block text-sm leading-4">is Required?</label>
                <UseFormToggle
                  id={'is_required'}
                  register={{ ...register('is_required') }}
                  setStatus={(data) => {
                    setValue('is_required', data, {
                      shouldDirty: true,
                    });
                  }}
                  initialstatus={tag.is_required || false}
                  data-testid="tag-required"
                ></UseFormToggle>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <label className="text-blue-dark mb-2 block text-sm leading-4">is Default? </label>
                <UseFormToggle
                  id={'isDefault?'}
                  register={{ ...register('is_default') }}
                  setStatus={(data: boolean) => {
                    setValue('is_default', data, {
                      shouldDirty: true,
                    });
                  }}
                  initialstatus={tag.is_default || false}
                  data-testid="tag-default"
                ></UseFormToggle>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <label className="text-blue-dark mb-2 block text-sm leading-4">Requires Documentation</label>
                <UseFormToggle
                  id={'isDefault?'}
                  register={{ ...register('is_background') }}
                  setStatus={(data: boolean) => {
                    setValue('is_background', data, {
                      shouldDirty: true,
                    });
                  }}
                  initialstatus={tag.is_background || false}
                  data-testid="tag-background"
                ></UseFormToggle>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

interface CreateTagModalProps {
  setShowCreateTagModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateTagModal: React.FC<CreateTagModalProps> = ({ setShowCreateTagModal }) => {
  const { mutate: createTag } = useCreateTag();

  const defaultNewTag = {
    is_visible: 'false',
    media: null,
    name: '',
    sort_order: 0,
    tag_type: 'topic',
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: defaultNewTag,
  });

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      is_visible: data.is_visible === 'true' ? true : false,
    };
    createTag(payload);
    setShowCreateTagModal(false);
    reset(defaultNewTag);
  };

  return (
    <>
      <Modal
        isModel={true}
        InnerComponent={
          <div className="inline-block max-w-3xl transform overflow-hidden rounded-lg border-2 border-black bg-white text-left transition-all">
            <div className="flex h-10 items-center justify-between px-6 pt-2">
              <div>Create New Tag</div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} id="create-tag-group">
              <div className="w-full px-3">
                <div className="mt-5 grid w-full grid-cols-1 gap-x-6 gap-y-6 px-7">
                  <div className="flex flex-col space-y-4">
                    <CustomInput
                      inputType="text"
                      Label="Tag Name"
                      register={{
                        ...register('name', {
                          required: 'Please provide Tag name',
                          onBlur: (e: any) => onBlur(e, 'name', setValue),
                        }),
                      }}
                      //@ts-ignore
                      Error={errors?.name?.message?.length ? true : false}
                      ErrorMessage={errors.name?.message}
                      dataTestId="tag-name"
                    />
                    <UseFormDropDown label={'Type'}>
                      <select
                        className="select-box w-full focus:ring-transparent"
                        defaultValue="published"
                        {...register('tag_type')}
                      >
                        <option value="topic">Topic</option>
                        <option value="trait">Trait</option>
                      </select>
                    </UseFormDropDown>
                    <UseFormDropDown label={'Visibility'}>
                      <select
                        className="select-box w-full focus:ring-transparent"
                        defaultValue="published"
                        {...register('is_visible')}
                        data-testid="tag-create-status"
                      >
                        <option value="true">Visible</option>
                        <option value="false">Hidden</option>
                      </select>
                    </UseFormDropDown>
                  </div>
                </div>
              </div>
            </form>
            <div className="flex w-full items-center justify-evenly py-2">
              <button
                className="bg-red h-6 w-20 rounded-full px-2 text-center text-sm text-white"
                onClick={() => {
                  reset(defaultNewTag);
                  setShowCreateTagModal(false);
                }}
                data-testid="close-tag-modal"
              >
                CLOSE
              </button>
              <div className={` ${!isDirty && 'cursor-not-allowed'}`}>
                <button
                  className={`ml-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white ${
                    isDirty ? 'bg-bright-blue' : 'bg-gray-background-dark pointer-events-none'
                  }`}
                  type="submit"
                  form="create-tag-group"
                  data-testid="save-tag-button"
                >
                  SAVE
                </button>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

interface FilterProps {
  count?: number;
  filters: TagRequestParams;
  setFilters: React.Dispatch<React.SetStateAction<TagRequestParams>>;
  setShowCreateTagModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Filters: React.FunctionComponent<FilterProps> = ({
  count,
  filters,
  setFilters,
  setShowCreateTagModal,
}): JSX.Element => {
  return (
    <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center justify-between border-b px-7">
      <div className="w-1/4">
        <Searchbar
          search={(data) => {
            setFilters((filters) => {
              return { ...filters, page: 1, search: data };
            });
          }}
        />
      </div>
      <div className="w-1/2">
        <Paginator
          currentPage={filters.page!}
          count={count || 0}
          limit={filters.limit!}
          handlePageChange={(page: number) => {
            setFilters((filters) => {
              return { ...filters, page: page };
            });
          }}
        />
      </div>
      <div className="w-1/4">
        <div className="flex items-center justify-between">
          <div className="gray-background-dark flex items-center justify-between py-4">
            {Object.keys(TagTypes).map((item) => (
              <button
                data-testid={`tag-type-${item}`}
                className={
                  'px-5 py-1 text-sm ' +
                  (TagTypes[item as keyof typeof TagTypes] === filters.tag_type
                    ? 'bg-blue-primary text-white'
                    : 'bg-gray-background-dark text-gray-dark')
                }
                onClick={() => {
                  setFilters((filters) => {
                    return { ...filters, tag_type: TagTypes[item as keyof typeof TagTypes], page: 1 };
                  });
                }}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="w-20">
            <button
              className="h-8 w-full whitespace-nowrap rounded-full bg-white px-2 py-1 text-center text-sm"
              onClick={() => {
                setShowCreateTagModal(true);
              }}
            >
              Add Tag
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tags;
